import { getHasSettledOnce } from '@/lib/bootGateStore';
import { maybeReleasePreReactBootSplash } from '@/lib/preReactBootHandoff';
import {
  BOOT_OVERLAY_HANDOFF_MS,
  isColdBootTransitionActive,
  pickActiveRequest,
  POST_LOGIN_TRANSITION_MS,
  resolveOwner,
  resolveVisibleSurface,
  SOURCE_PHASE,
  SOURCE_PRIORITY,
} from './runtimePolicy';
import type {
  CoordinatorVisibleState,
  LoadingAuditEvent,
  LoadingOwner,
  LoadingPhase,
  LoadingRequest,
  LoadingSource,
  LoadingSurfaceVariant,
} from './types';

function newTransitionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

let transitionId = newTransitionId();
let requests = new Map<string, LoadingRequest>();
let postLoginUntil = 0;
let suppressUntil = 0;
let bootHandoffAt: number | null = null;
const auditEvents: LoadingAuditEvent[] = [];
const listeners = new Set<() => void>();

let cachedVisible: CoordinatorVisibleState = buildVisibleState();

function notify(): void {
  const requestList = [...requests.values()];
  const coldBootTransitionActive = isColdBootTransitionActive(requestList);
  cachedVisible = buildVisibleState();
  maybeReleasePreReactBootSplash(coldBootTransitionActive);
  listeners.forEach((fn) => fn());
}

function buildVisibleState(): CoordinatorVisibleState {
  const requestList = [...requests.values()];
  const isWarmNav = getHasSettledOnce();
  const coldBootTransitionActive = isColdBootTransitionActive(requestList);
  const activeRequest = pickActiveRequest(requestList, {
    isWarmNav,
    postLoginUntil,
    suppressUntil,
  });
  const surface = resolveVisibleSurface(activeRequest, isWarmNav, { coldBootTransitionActive });
  const owner = resolveOwner(activeRequest);

  return {
    transitionId,
    owner,
    phase: activeRequest?.phase ?? 'background',
    surface,
    activeRequest,
    routePath: activeRequest?.routePath,
    suppressUntil,
    isWarmNav,
    startedAt: activeRequest?.startedAt ?? 0,
  };
}

function pushAuditEvent(owner: LoadingOwner, surface: LoadingSurfaceVariant, source: LoadingSource | 'none'): void {
  const event: LoadingAuditEvent = {
    type: 'owner-changed',
    at: Date.now(),
    transitionId,
    owner,
    surface,
    source,
  };
  auditEvents.push(event);
  if (auditEvents.length > 500) auditEvents.shift();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('loading:owner-changed', { detail: event }));
  }
}

let lastAuditKey = '';

function maybeEmitAudit(state: CoordinatorVisibleState): void {
  const key = `${state.transitionId}:${state.owner}:${state.surface}:${state.activeRequest?.source ?? 'none'}`;
  if (key === lastAuditKey) return;
  lastAuditKey = key;
  pushAuditEvent(state.owner, state.surface, state.activeRequest?.source ?? 'none');
}

export function getCoordinatorVisibleState(): CoordinatorVisibleState {
  return cachedVisible;
}

export function subscribeCoordinator(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function requestLoading(input: {
  id: string;
  source: LoadingSource;
  routePath?: string;
  label?: string;
}): void {
  const req: LoadingRequest = {
    id: input.id,
    source: input.source,
    phase: SOURCE_PHASE[input.source],
    priority: SOURCE_PRIORITY[input.source],
    routePath: input.routePath,
    startedAt: Date.now(),
    label: input.label,
  };
  requests.set(input.id, req);
  notify();
  maybeEmitAudit(cachedVisible);
}

export function releaseLoading(id: string): void {
  if (!requests.has(id)) return;
  requests.delete(id);
  notify();
  maybeEmitAudit(cachedVisible);
}

export function beginTransition(routePath?: string): void {
  transitionId = newTransitionId();
  suppressUntil = Date.now() + 300;
  if (routePath) {
    void routePath;
  }
  notify();
}

export function markPostLoginTransition(): void {
  postLoginUntil = Date.now() + POST_LOGIN_TRANSITION_MS;
  transitionId = newTransitionId();
  suppressUntil = Date.now() + 400;
  notify();
}

export function markBootHandoffComplete(): void {
  bootHandoffAt = Date.now();
}

export function getBootHandoffRemainingMs(): number {
  if (bootHandoffAt === null) return 0;
  const elapsed = Date.now() - bootHandoffAt;
  return Math.max(0, BOOT_OVERLAY_HANDOFF_MS - elapsed);
}

export function getLoadingAuditEvents(): LoadingAuditEvent[] {
  return [...auditEvents];
}

/** Serializable coordinator snapshot for audit poll / video acceptance. */
export function getLoadingCoordinatorSnapshot(): {
  timestamp: number;
  transitionId: string;
  owner: LoadingOwner;
  phase: LoadingPhase;
  source: LoadingSource | 'none';
  surface: LoadingSurfaceVariant;
  routePath: string | null;
  isWarmNav: boolean;
  suppressUntil: number;
  activeRequestId: string | null;
  eventCount: number;
} {
  const state = getCoordinatorVisibleState();
  return {
    timestamp: Date.now(),
    transitionId: state.transitionId,
    owner: state.owner,
    phase: state.phase,
    source: state.activeRequest?.source ?? 'none',
    surface: state.surface,
    routePath: state.routePath ?? null,
    isWarmNav: state.isWarmNav,
    suppressUntil: state.suppressUntil,
    activeRequestId: state.activeRequest?.id ?? null,
    eventCount: auditEvents.length,
  };
}

export function resetCoordinatorForTests(): void {
  transitionId = newTransitionId();
  requests.clear();
  postLoginUntil = 0;
  suppressUntil = 0;
  bootHandoffAt = null;
  auditEvents.length = 0;
  lastAuditKey = '';
  notify();
}

/** Count distinct visual phases from audit events (for acceptance). */
export function computePhaseCountByEye(events: LoadingAuditEvent[] = auditEvents): number {
  let count = 0;
  let lastKey = '';
  for (const e of events) {
    if (e.surface === 'none') continue;
    const key = `${e.owner}:${e.surface}`;
    if (key !== lastKey) {
      count += 1;
      lastKey = key;
    }
  }
  return count;
}
